import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react'

// Cognito configuration
const COGNITO_DOMAIN = 'https://fd-qa-tools.auth.us-west-2.amazoncognito.com'
const COGNITO_CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID || 'REPLACE_AFTER_DEPLOYMENT'
const REDIRECT_URI = window.location.origin + '/auth/callback'
const LOGOUT_URI = window.location.origin + '/'

// Proactively refresh tokens this many ms before they expire (5 minutes)
const REFRESH_BUFFER_MS = 5 * 60 * 1000
// How often an open page re-checks the token. Without this, a page left open
// (an RT Audit progress screen, say) kept its ID token past the hour Cognito
// gives it, every API call then failed with 401, and the page froze.
const AUTH_CHECK_INTERVAL_MS = 60 * 1000

// Session storage keys written by login(), read by AuthCallback
export const PKCE_VERIFIER_KEY = 'fd_qa_tools_pkce_verifier'
export const OAUTH_STATE_KEY = 'fd_qa_tools_oauth_state'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  login: () => Promise<void>
  logout: () => void
  getAccessToken: () => string | null
}

interface User {
  email: string
  name?: string
  givenName?: string
  familyName?: string
  sub: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<User | null>(null)

  // Prevents concurrent checkAuth invocations (e.g. rapid tab switches or
  // the browser firing multiple visibilitychange events when waking from sleep).
  // Using a ref so the check-and-set is synchronous and never causes re-renders.
  const isRefreshing = useRef(false)

  // Stable ref to always call the latest checkAuth without stale-closure issues.
  // The useEffect below captures this ref once (empty deps), but checkAuthRef.current
  // is updated on every render so visibilitychange always invokes the latest version.
  const checkAuthRef = useRef<() => Promise<void>>(() => Promise.resolve())

  // Check auth on mount and whenever the user returns to the tab.
  // This ensures tokens that expired while the tab was idle get refreshed
  // before the next API call is made.
  useEffect(() => {
    checkAuthRef.current()

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkAuthRef.current()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    const timer = window.setInterval(() => checkAuthRef.current(), AUTH_CHECK_INTERVAL_MS)
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.clearInterval(timer)
    }
  }, [])

  const checkAuth = async () => {
    if (isRefreshing.current) return
    isRefreshing.current = true

    try {
      const accessToken = localStorage.getItem('accessToken')
      const idToken = localStorage.getItem('idToken')
      const expiresAt = localStorage.getItem('expiresAt')

      if (!accessToken || !idToken || !expiresAt) {
        setIsLoading(false)
        return
      }

      // Refresh if expired or expiring within the buffer window
      const now = Date.now()
      if (now >= Number(expiresAt) - REFRESH_BUFFER_MS) {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          await refreshAccessToken(refreshToken)
        } else {
          clearAuth()
          setIsLoading(false)
        }
        return
      }

      const userInfo = parseJwt(idToken)
      if (!userInfo?.email || !userInfo?.sub) {
        // Corrupted token in storage — clear and force re-login
        clearAuth()
        setIsLoading(false)
        return
      }

      setUser(extractUser(userInfo))
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Error checking auth:', error)
      clearAuth()
    } finally {
      isRefreshing.current = false
      setIsLoading(false)
    }
  }

  // Keep ref current on every render so the visibilitychange handler always
  // calls the latest checkAuth (avoids stale closure over state setters).
  checkAuthRef.current = checkAuth

  const login = async () => {
    const verifier = generateCodeVerifier()
    const challenge = await generateCodeChallenge(verifier)
    const state = generateState()

    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier)
    sessionStorage.setItem(OAUTH_STATE_KEY, state)

    const authUrl =
      `${COGNITO_DOMAIN}/oauth2/authorize?` +
      `client_id=${COGNITO_CLIENT_ID}&` +
      `response_type=code&` +
      `scope=openid+email+profile&` +
      `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
      `identity_provider=Google&` +
      `code_challenge=${challenge}&` +
      `code_challenge_method=S256&` +
      `state=${encodeURIComponent(state)}`

    window.location.href = authUrl
  }

  const logout = () => {
    clearAuth()
    const logoutUrl =
      `${COGNITO_DOMAIN}/logout?` +
      `client_id=${COGNITO_CLIENT_ID}&` +
      `logout_uri=${encodeURIComponent(LOGOUT_URI)}`
    window.location.href = logoutUrl
  }

  const getAccessToken = (): string | null => {
    return localStorage.getItem('accessToken')
  }

  const clearAuth = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('expiresAt')
    setIsAuthenticated(false)
    setUser(null)
  }

  const refreshAccessToken = async (refreshToken: string) => {
    try {
      const response = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: COGNITO_CLIENT_ID,
          refresh_token: refreshToken,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to refresh token')
      }

      const data = await response.json()
      storeTokens(data)

      const userInfo = parseJwt(data.id_token)
      if (!userInfo?.email || !userInfo?.sub) {
        clearAuth()
        return
      }

      setUser(extractUser(userInfo))
      setIsAuthenticated(true)
    } catch (error) {
      console.error('Error refreshing token:', error)
      // clearAuth handles state cleanup; do not re-throw so checkAuth's
      // finally block can run without a second clearAuth call.
      clearAuth()
    }
  }

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, user, login, logout, getAccessToken }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function extractUser(payload: Record<string, string>): User {
  const givenName = payload.given_name || ''
  const familyName = payload.family_name || ''
  const fullName = [givenName, familyName].filter(Boolean).join(' ') || payload.name
  return { email: payload.email, name: fullName, givenName, familyName, sub: payload.sub }
}

export function parseJwt(token: string): Record<string, string> | null {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Error parsing JWT:', error)
    return null
  }
}

export function storeTokens(data: Record<string, string | number>) {
  const now = Date.now()
  const expiresAt = now + Number(data.expires_in) * 1000
  localStorage.setItem('accessToken', String(data.access_token))
  localStorage.setItem('idToken', String(data.id_token))
  if (data.refresh_token) {
    localStorage.setItem('refreshToken', String(data.refresh_token))
  }
  localStorage.setItem('expiresAt', expiresAt.toString())
}

function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

function generateState(): string {
  const array = new Uint8Array(16)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export { COGNITO_DOMAIN, COGNITO_CLIENT_ID, REDIRECT_URI }
